/* @flow */
import * as React from "react";
import styled, { css } from "styled-components";
import moment from "moment";
import Gettext, { withGettext } from "../common/Gettext";
import { rtl, fontSize } from "../common/styled-mixins";
import Input from "../Input";
import Button from "../Button";
import Money from "../Money";
import { useServerTime } from "../Protection";
import { PromoContext } from "../../pages/fillup";
import coupon from "../../images/promocode/coupon.svg";
import dots from "../../images/promocode/promo-dots.svg";
import couponIcon from "../../images/promocode/coupon-icon.svg";
import infoIcon from "../../images/promocode/info-icon.svg";
import timerIcon from "../../images/promocode/timer-icon.svg";
import walletIcon from "../../images/promocode/wallet-icon.svg";

const ErrorIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="6341 408.95 18 16"
    fill="#e74c3c"
    width="18"
    height="16"
  >
    <path
      d="M17.7,42.525,10.62,30.759A2.1,2.1,0,0,0,7,30.768L.3,42.52a2.121,2.121,0,0,0,1.812,3.209H15.886A2.121,2.121,0,0,0,17.7,42.525ZM8.811,43.607a1.061,1.061,0,1,1,1.055-1.061A1.059,1.059,0,0,1,8.811,43.607ZM9.88,38.3a1.071,1.071,0,0,1-1.069,1.061A1.053,1.053,0,0,1,7.764,38.3V34.061A1.053,1.053,0,0,1,8.811,33,1.071,1.071,0,0,1,9.88,34.061Z"
      transform="translate(6340.999 379.221)"
    />
  </svg>
);

const BonusDeposit = ({ bonus }) => {
  if (bonus.min_dep === null) {
    return (
      <Td>
        <Gettext>up to {<Money>{bonus.max_dep}</Money>}</Gettext>
      </Td>
    );
  } else if (bonus.max_dep === null) {
    return (
      <Td>
        <Gettext>from {<Money>{bonus.min_dep}</Money>}</Gettext>
      </Td>
    );
  } else {
    return (
      <Td>
        <Gettext>from {<Money>{bonus.min_dep}</Money>}</Gettext>{" "}
        <Gettext>up to {<Money>{bonus.max_dep}</Money>}</Gettext>
      </Td>
    );
  }
};

export const BonusTable = props => (
  <Table>
    <tbody>
      <tr>
        <Th>
          <Gettext>Deposit amount</Gettext>
        </Th>
        <Th>
          <Gettext>Bonus amount</Gettext>
        </Th>
      </tr>
      {props.promo.size_of_bonus.map(bonus => {
        return (
          <tr>
            <BonusDeposit bonus={bonus} />
            <Td>
              <Money>{bonus.bonus}</Money>
            </Td>
          </tr>
        );
      })}
    </tbody>
  </Table>
);

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: 25px;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 20000;
  overflow: auto;

  ${props =>
    !props.info &&
    css`
      @media (max-width: 767px) {
        padding: 0;
      }
    `};
`;

const PopupTable = styled.table`
  table-layout: fixed;
  width: 100%;
  height: 100%;
  margin: 0 auto;
`;

const Popup = styled.div`
  direction: ${({ theme }) => theme.direction};
  position: relative;
  display: inline-flex;
  justify-content: flex-start;
  align-items: stretch;
  width: 100%;
  min-height: ${({ info }) => (info ? "auto" : "260px")};
  margin: 0 auto;
  text-align: center;
  background-color: #fff;
  border-radius: 4px;
  box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 16);
  overflow: hidden;

  ${props =>
    !props.info &&
    css`
      @media (max-width: 767px) {
        min-height: 100%;
        height: 100%;
        max-width: 100%;
        border-radius: 0;
        box-shadow: none;
        background-color: #f6f6f6;
      }

      @media (min-width: 960px) {
        width: auto;
        min-width: 620px;
        max-width: 700px;
      }
    `};
`;

const Close = styled.div`
  position: absolute;
  top: 14px;
  ${rtl("right", "14px")};
  width: 16px;
  height: 16px;
  color: #a0a7b2;
  background-size: cover;
  transition: all 0.3s ease;
  cursor: pointer;
  z-index: 1;

  :before,
  :after {
    content: "";
    position: absolute;
    top: -1px;
    left: 50%;
    width: 0;
    height: 22px;
    border-left: 2px solid;
  }

  :before {
    transform: translateX(-50%) rotate(45deg);
    transform-origin: 100%;
  }

  :after {
    transform: translateX(-50%) rotate(-45deg);
    transform-origin: 0;
  }

  :hover {
    color: #60a31c;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;

  @media (max-width: 567px) {
    flex-direction: column;
  }
`;

const ImgContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fff;

  @media (min-width: 568px) {
    width: 175px;
    min-width: 175px;

    :after {
      position: absolute;
      ${rtl("right", "-2px")};
      top: 4px;
      bottom: 0;
      height: 100%;
      width: 4px;
      background: url(${dots});
      background-repeat: repeat-y;
      content: "";
    }
  }
  @media (min-width: 960px) {
    width: 225px;
    min-width: 225px;
  }
  @media (max-width: 767px) {
    padding: 30px 35px;
  }
  @media (max-width: 567px) {
    background-color: #f6f6f6;
  }
`;

const CouponImg = styled.img`
  display: block;
  width: 177px;

  @media (min-width: 568px) and (max-width: 959px) {
    width: 127px;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 25px;
  padding-bottom: 16px;
  background-color: #f6f6f6;

  @media (max-width: 959px) {
    padding: 25px 15px;
  }

  @media (max-width: 567px) {
    flex: 1;
    align-items: center;
    max-width: 500px;
    margin-left: auto;
    margin-right: auto;
    padding-top: 0;
  }

  @media (max-width: 359px) {
    padding: 25px 10px;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 288px;
  padding-top: 11px;
`;

const Title = styled.div`
  direction: ${props => props.theme.direction};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  font-size: 16px;
  font-weight: bold;
  color: #343434;

  @media (max-width: 567px) {
    flex-direction: column;
    justify-content: center;
    width: 100%;
    text-align: center;
  }
`;

const InputContainer = styled.div`
  width: 100%;
  max-width: 288px;
  margin: 18px auto 28px auto;

  @media (max-width: 567px) {
    max-width: 280px;
  }

  @media (max-width: 479px) {
    max-width: 177px;
  }

  input {
    text-transform: uppercase;
  }
`;

const ButtonContainer = styled.div`
  margin-bottom: 28px;

  @media (max-width: 567px) {
    display: inline-block;
    margin-top: 16px;
  }
`;

const PromoInfo = styled.div`
  font-size: ${fontSize(12)};
  line-height: 20px;
  color: #919191;
  text-align: center;

  ${props =>
    props.theme.direction === "rtl" &&
    css`
      text-align: right;
    `};

  ${props =>
    props.desktop &&
    css`
      @media (max-width: 567px) {
        display: none;
      }
    `};

  ${props =>
    props.mobile &&
    css`
      @media (min-width: 960px) {
        display: none;
      }
    `};
`;

const PromoError = styled(PromoInfo)`
  display: flex;
  color: #e74c3c;
`;

const ValidImg = styled.div`
  width: 20px;
  margin-left: 7px;

  ${props =>
    props.theme.direction === "rtl" &&
    css`
      margin-left: 0;
      margin-right: 7px;
    `};
`;

const ContinueContainer = styled.div`
  margin-top: 10px;
`;

const ErrorIconContainer = styled.div`
  width: 18px;
  min-width: 18px;
  margin-right: 7px;
`;

const CancelContainer = styled.div`
  width: 100%;
  margin-top: 15px;
  margin-bottom: 15px;
  text-align: center;
  display: ${props => (props.hide ? "none" : "block")};
`;

const Cancel = styled.button`
  display: inline;
  padding: 0;
  font-weight: normal;
  font-size: ${fontSize(14)};
  color: #3598db;
  text-decoration: underline;
  background: none;
  border: none;
  cursor: pointer;

  :hover {
    text-decoration: none;
  }
`;

const InfoContainer = styled.div`
  padding: 17px;
  background-color: #f6f6f6;
`;

const InfoIcon = styled.div`
  display: block;
  width: 100%;
  margin-bottom: 20px;
  text-align: left;
`;

const InfoText = styled.div`
  ${rtl("text-align", "left")};
  font-size: ${fontSize(12)};
  line-height: 1.2;
  color: #343434;
`;

const Table = styled.table`
  margin-top: 5px;
`;

const Th = styled.th`
  padding: 5px 8px;
  font-size: ${fontSize(14)};
  font-weight: bold;
  line-height: 1.3;
  border: 1px solid #717171;
`;

const Td = styled.td`
  padding: 5px 8px;
  font-size: ${fontSize(14)};
  line-height: 1.2;
  border: 1px solid #717171;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  min-height: 27px;
  margin-bottom: 7px;

  @media (max-width: 475px) {
    ${({ isPromo, theme }) =>
      theme.locale === "es" &&
      isPromo &&
      css`
        flex-wrap: wrap;
      `};
  }
`;

const IconContainer = styled.div`
  width: 20px;
  ${rtl("margin-right", "20px")};
  flex: 0 0 auto;

  @media (max-width: 639px) {
    ${rtl("margin-right", "10px")};
  }
`;

const IconImage = styled.img`
  max-width: 100%;
`;

const TitleText = styled.div`
  direction: ${({ theme }) => theme.direction};
  font-size: 16px;
  font-weight: bold;
  color: #343434;
  flex: 0 0 auto;
  ${rtl("margin-right", "20px")};
  white-space: nowrap;

  @media (max-width: 639px) {
    ${rtl("margin-right", "10px")};
  }

  @media (max-width: 375px) {
    font-size: ${fontSize(14, 16)};
  }

  @media (max-width: 359px) {
    flex-shrink: 1;
    white-space: normal;
    ${rtl("text-align", "left")};
    line-height: 1.2;
  }
`;

const Text = styled.div`
  width: ${({ upper }) => (upper ? "auto" : "100%")};
  font-size: 16px;
  font-weight: bold;
  line-height: 1.2;
  ${rtl("text-align", "left")};
  text-transform: ${({ upper }) => (upper ? "uppercase" : "none")};
  color: ${({ timer }) => (timer ? "#0085EB" : "#3B9478")};

  @media (max-width: 375px) {
    font-size: ${fontSize(14, 16)};
  }

  @media (max-width: 359px) {
    flex: 1;
    white-space: ${({ timer }) => (timer ? "nowrap" : "normal")};
  }
`;

const PromoCodeContainer = styled.div`
  display: flex;
`;

const Bonus = ({ promo }) => {
  if (promo.size_of_bonus == null) {
    return null;
  }

  if (promo.size_of_bonus.length === 1) {
    // type_of_bonus === "money" | "percent"

    if (promo.type_of_bonus === "percent") {
      return (
        <Gettext>
          {""}
          {`${promo.size_of_bonus[0].bonus}%`} for any sum
        </Gettext>
      );
    }

    return (
      <Gettext>
        {""}
        {<Money>{promo.size_of_bonus[0].bonus}</Money>} for any sum
      </Gettext>
    );
  }

  const l = promo.size_of_bonus.length - 1;

  const sortedBonus = promo.size_of_bonus.sort((a, b) =>
    a.bonus > b.bonus ? 1 : -1
  );

  if (promo.type_of_bonus === "percent") {
    return (
      // prettier-ignore
      <Gettext>
        From {`${sortedBonus[0].bonus}%`} to {`${sortedBonus[l].bonus}%`}
      </Gettext>
    );
  }

  return (
    // prettier-ignore
    <Gettext>
      From {<Money>{sortedBonus[0].bonus}</Money>} to <Money>{sortedBonus[l].bonus}</Money>
    </Gettext>
  );
};

type PromoProps = {
  onConfirm: Function,
  onDismiss: Function,
  onValidatePromo: Function,
  promoCode: string
};

const Timer = ({ time }) => {
  const duration = moment.duration(time, "seconds");
  const d = duration.days();
  const h = duration.hours();
  const m = duration.minutes();

  return (
    <span>
      {d}
      <Gettext>d</Gettext> : {h}
      <Gettext>h</Gettext> : {m}
      <Gettext>m</Gettext>
    </span>
  );
};

export const FullPage = ({ children, info }) => {
  return (
    <Overlay info={info}>
      <PopupTable>
        <tbody>
          <tr>
            <td style={{ verticalAlign: "middle", textAlign: "center" }}>
              <Popup info={info}>{children}</Popup>
            </td>
          </tr>
        </tbody>
      </PopupTable>
    </Overlay>
  );
};

const ModalPromo = (props: PromoProps) => {
  const promoContext = React.useContext(PromoContext);
  const currentTime = useServerTime([]);

  return (
    <FullPage info={props.info}>
      <Close
        onClick={
          props.info
            ? props.toggleInfoModal
            : () => {
                props.onRemovePromo();
                props.onDismiss();
              }
        }
      />
      {props.info ? (
        <InfoContainer>
          <InfoIcon>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="2025 3000 36 36"
              fill="#feac00"
              width="36"
              height="36"
            >
              <g id="information" transform="translate(2025 3000)">
                <path
                  d="M18,0A18,18,0,1,0,36,18,18,18,0,0,0,18,0Zm0,8.394a2.26,2.26,0,1,1-2.26,2.26A2.26,2.26,0,0,1,18,8.394Zm3.955,19.211h-7.91v-3.39H15.74v-6.78H14.045v-3.39H20.26v10.17h1.695v3.39h0Z"
                  transform="translate(0)"
                />
                <path
                  d="M325.935,331.7l7.9,2.116-2.116-7.9Z"
                  transform="translate(-298.434 -298.419)"
                />
              </g>
            </svg>
          </InfoIcon>
          <InfoText>
            {promoContext.isPromoApplied ? (
              <React.Fragment>
                {promoContext.promo.description}{" "}
                {promoContext.promo.type_of_bonus === "money" && (
                  <BonusTable promo={promoContext.promo} />
                )}
              </React.Fragment>
            ) : (
              <React.Fragment>
                <Gettext>
                  A promo code is a special set of symbols that allows you to
                  receive a bonus.
                </Gettext>
                <br />
                <Gettext>
                  Enter a code into the field and press "Confirm". The promo
                  code will activate extra funds to your account.
                </Gettext>
              </React.Fragment>
            )}
          </InfoText>
        </InfoContainer>
      ) : (
        <Container>
          <ImgContainer>
            <CouponImg src={coupon} alt="" />
          </ImgContainer>
          {promoContext.isPromoApplied ? (
            <Content>
              <ContentWrapper>
                <Row isPromo>
                  <IconContainer>
                    <IconImage src={couponIcon} />
                  </IconContainer>
                  <TitleText>
                    <Gettext>Promo Code</Gettext>:
                  </TitleText>
                  <PromoCodeContainer>
                    <Text upper>{promoContext.promoCode}</Text>
                    <ValidImg>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="13.487"
                        height="10.209"
                        viewBox="0 0 13.487 10.209"
                      >
                        <path
                          d="M5.694,30.593l-3.35-3.374L1,28.573,5.694,33.3l8.793-8.856-1.344-1.353Z"
                          transform="translate(-1 -23.091)"
                          fill="#3b9478"
                        />
                      </svg>
                    </ValidImg>
                  </PromoCodeContainer>
                </Row>
                <Row>
                  <IconContainer>
                    <IconImage src={walletIcon} />
                  </IconContainer>
                  <TitleText>
                    <Gettext>Bonus</Gettext>:
                  </TitleText>
                  <Text>
                    <Bonus promo={promoContext.promo} />
                  </Text>
                </Row>
                <Row>
                  <IconContainer>
                    <IconImage src={timerIcon} />
                  </IconContainer>
                  <TitleText>
                    <Gettext>Bonus lifetime</Gettext>:
                  </TitleText>
                  <Text timer>
                    <Timer
                      time={promoContext.promo.expire_time - currentTime}
                    />
                  </Text>
                </Row>
                {promoContext.promo.description && (
                  <Row>
                    <IconContainer>
                      <IconImage src={infoIcon} />
                    </IconContainer>
                    <InfoText>{promoContext.promo.description}</InfoText>
                  </Row>
                )}

                <ContinueContainer>
                  <Button
                    onClick={props.onDismiss}
                    title={props.gettext`Confirm`}
                    wide
                    color="mint"
                  />
                </ContinueContainer>

                <CancelContainer>
                  <Cancel
                    onClick={() => {
                      props.onRemovePromo();
                      props.onDismiss();
                    }}
                  >
                    <Gettext>Cancel</Gettext>
                  </Cancel>
                </CancelContainer>

                <PromoInfo>
                  <Gettext>
                    A promo code is a special set of symbols that allows you to
                    receive a bonus.
                  </Gettext>
                </PromoInfo>
              </ContentWrapper>
            </Content>
          ) : (
            <Content>
              <ContentWrapper>
                <Title>
                  <span>
                    <Gettext>Promo Code</Gettext>:
                  </span>
                </Title>
                <InputContainer>
                  <Input
                    onChange={promoContext.onInputPromo}
                    invalid={!!promoContext.promo_error}
                    value={promoContext.promoCode}
                    focus
                    promo
                  />
                </InputContainer>
                <ButtonContainer>
                  <Button
                    onClick={promoContext.onValidatePromo}
                    title={props.gettext`Apply`}
                    showSpinner={promoContext.isFetchPromoInProgress}
                    disabled={
                      promoContext.isFetchPromoInProgress ||
                      promoContext.promo_error ||
                      promoContext.promoCode.length < 1
                    }
                    color="mint"
                    wide
                  />
                </ButtonContainer>
                {promoContext.promo_error ? (
                  <PromoError>
                    <ErrorIconContainer>
                      <ErrorIcon />
                    </ErrorIconContainer>
                    {promoContext.promo_error}
                  </PromoError>
                ) : (
                  <PromoInfo>
                    <Gettext>
                      A promo code is a special set of symbols that allows you
                      to receive a bonus.
                    </Gettext>
                  </PromoInfo>
                )}
                <CancelContainer hide>
                  <Cancel onClick={props.onDismiss}>
                    <Gettext>Cancel</Gettext>
                  </Cancel>
                </CancelContainer>
              </ContentWrapper>
            </Content>
          )}
        </Container>
      )}
    </FullPage>
  );
};

export default withGettext(ModalPromo);



// WEBPACK FOOTER //
// ./src/components/Fillup/ModalPromo.js